import ConfirmModal from 'components/ConfirmModal'
import I from 'components/I'
import Icon from 'components/Icon'
import { map, omit } from 'lodash-es'
import OrderingStatus from 'pages/Status/OrderingStatus'
import PropTypes from 'prop-types'
import { Alert, Form } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Restaurant from 'state/models/Restaurant'
import { getHasReseller, getIsCMO } from 'state/Reseller'
import { toggleRestaurant } from 'state/Restaurants'
import { platform, useSimpleState } from 'utils'

/**
 * @param show {boolean}
 * @param restaurant {Restaurant}
 * @param externalClose {function}
 * @returns {JSX.Element}
 * @constructor
 */
export default function DisableOrderingModal({ show, restaurant, doClose: externalClose }) {
	const { t }      = useTranslation()
	const dispatch   = useDispatch()
	const hasAdmin   = !useSelector(getHasReseller)
	const isCMO      = useSelector(getIsCMO)
	const DEF_PERIOD = '-0'

	const selectedPeriods = useSimpleState({})

	let options = {
		30  : t('{{count}} minute', { count: 30 }),
		60  : t('{{count}} hour', { count: 1 }),
		120 : t('{{count}} hour', { count: 2 }),
		240 : t('{{count}} hour', { count: 4 }),
		'-0': t('Rest of day'), //the minus makes Firefox "respect" the order and place this at the bottom :| //TODO: this should be a constant coming from the backend
		'-1': t('Indefinitely')
	}
	if (platform.isDev()) { //not sure why this doesn't work outside, where OrderingToggle.OPTIONS is defined?
		options[5] = '5min [dev only]'
	}
	if (isCMO) {
		delete options['-1']
	}

	function doSelectType({ target }) {
		target.checked?
			selectedPeriods.merge({ [target.value]: DEF_PERIOD }) :
			selectedPeriods.set(omit(selectedPeriods.val, target.value))
	}

	function doSelectPeriod({ target: { value } }, orderType) {
		selectedPeriods.merge({ [orderType]: value })
	}

	function doClose() { //so the values aren't kept when reopening
		selectedPeriods.set({})
		externalClose()
	}

	async function doSubmit() {
		await dispatch(toggleRestaurant(restaurant, selectedPeriods.val))
		doClose()
	}

	const canSubmit = Object.keys(selectedPeriods).length
	return <ConfirmModal {...{ show, doClose, doSubmit }} id="modal-ordering" size="md"
	                     disabled={!canSubmit}
				               confirmTexts={[t('Save'), t('Saving'), t('Saved')]}
					             cancelText={t('Cancel', { context: 'go back' })}
					             title={t('Online ordering at {{restaurantName}}', {restaurantName: restaurant.display_name})}>
			<Alert variant="info">
				<Icon i="circle-info" className="text-info mr-2"/>
				<b>{t('This will not affect your usual business hours.')}</b>{' '}
				{hasAdmin?
					<Trans>If you need that, go to the <a href={restaurant.adminUrl('locationhours')}>Admin</a> or contact support
						for help.</Trans> :
					<Trans>If you need to change that, please contact support for assistance.</Trans>
				}
			</Alert>

			<Form>
				<Form.Label><b>{t('Individual order types:')}</b></Form.Label>
				{restaurant.enabled_base_order_types.map(type =>
					<OrderingFormGroup type={type} isClosed={restaurant.isPublicOrderingClosedForType(type)}/>
				)}

				<hr/>

				<Form.Label className="font-weight-bold">
					<b><Trans>Or change <I zh>ALL</I> order types at once:</Trans></b>
				</Form.Label>
				<OrderingFormGroup type={'ALL'} isClosed={restaurant.isPublicOrderingClosedForAllTypes()}
				                   showOrderingStatus={false} />
			</Form>
	</ConfirmModal>

	/**
	 * @param type {'ALL'|string}
	 * @param isClosed {boolean}
	 * @param showOrderingStatus {boolean}
	 * @returns {JSX.Element}
	 */
	function OrderingFormGroup({ type, isClosed, showOrderingStatus = true }) {
		const checked    = (type in selectedPeriods.val)  //TODO create a has() on useSimpleState?
		const checkLabel = (type == 'ALL')?
			(isClosed? t('Enable all order types') : t('Disable all for', { context: '...a period' })) :
			(isClosed? t('Enable immediately') : t('Disable for', { context: '...a period' }))

		return <Form.Group>

			<div className="I-need-checkboxes-here">
				{showOrderingStatus && <OrderingStatus restaurant={restaurant} type={type}/>}

				<Form.Check value={type} id={type} key={type} label={checkLabel} checked={checked} onChange={doSelectType}/>

				{!isClosed &&
					<Form.Control as="select" defaultValue={selectedPeriods.val[type] || DEF_PERIOD}
					              onChange={e => doSelectPeriod(e, type)}
					              disabled={!checked}>
						{map(options, (label, id) => <option value={id} key={id}>{label}</option>)}
					</Form.Control>
				}
			</div>
		</Form.Group>
	}
}

DisableOrderingModal.propTypes = {
	show      : PropTypes.bool.isRequired,
	restaurant: PropTypes.instanceOf(Restaurant).isRequired,
	doClose   : PropTypes.func.isRequired,
}
